<template>
  <b-modal :visible="true" centered :no-close-on-backdrop="busy" :no-close-on-esc="busy" :hide-header-close="busy"
           @hidden="$emit('close')" @hide="onHide" body-class="cd-modal-body">
    <template #modal-title>
      <i class="fas mr-1" :class="mode === 'assign' ? 'fa-plus-circle text-success' : 'fa-undo'"></i>
      {{ mode === 'assign' ? 'Assign trees' : 'Release trees' }} · {{ company.name }}
    </template>

    <p v-if="loading" class="cd-intro"><i class="fas fa-circle-notch fa-spin"></i> Loading forests…</p>
    <p v-else-if="!forests.length" class="cd-intro">
      {{ mode === 'assign'
        ? 'No forest has trees left to assign. Only trees that were never minted and have no splits delivered can be assigned.'
        : 'This company has no trees that can be released. Trees with splits already delivered stay with the company.' }}
    </p>
    <template v-else>
      <p class="cd-intro">
        {{ mode === 'assign'
          ? '1. Choose the forest the trees come from. Each one shows how many trees are free.'
          : '1. Choose the forest to take trees back from. Only trees nobody has used can be released.' }}
      </p>
      <div class="cd-list" role="radiogroup" aria-label="Forest">
        <label v-for="f in forests" :key="f.areaId" class="cd-option" :class="{ on: areaId === f.areaId }">
          <input type="radio" name="forest" :value="f.areaId" :checked="areaId === f.areaId" @change="pick(f.areaId)">
          <span class="cd-option-name">{{ f.forest }}</span>
          <span class="cd-option-count"><b>{{ fmt(f.trees) }}</b> trees · {{ fmt(f.splits) }} splits</span>
        </label>
      </div>
      <template v-if="selected">
        <p class="cd-intro">2. How many trees?</p>
        <div class="cd-count cd-fld">
          <input v-model.number="count" type="number" min="1" :max="selected.trees" aria-label="Number of trees">
          <button type="button" class="cd-btn" @click="count = selected.trees">All ({{ fmt(selected.trees) }})</button>
        </div>
        <p class="cd-intro" :class="{ 'text-danger': !valid }">
          <template v-if="valid">{{ fmt(count) }} tree{{ count === 1 ? '' : 's' }} = {{ fmt(count * perTree) }} splits.</template>
          <template v-else>Enter a whole number between 1 and {{ fmt(selected.trees) }}.</template>
        </p>
      </template>
    </template>
    <div v-if="error" class="cd-error"><i class="fas fa-exclamation-circle"></i> {{ error }}</div>

    <template #modal-footer>
      <button type="button" class="cd-btn ghost" :disabled="busy" @click="$emit('close')">Cancel</button>
      <button type="button" class="cd-btn primary" :disabled="!valid || busy" @click="confirm">
        <i v-if="busy" class="fas fa-circle-notch fa-spin"></i> {{ busy ? 'Working…' : (mode === 'assign' ? 'Assign' : 'Release') }}
      </button>
    </template>
  </b-modal>
</template>

<script>
import Companies, { apiError } from '../../api/Companies';

/** Assign trees to a company, or take untouched ones back: first the forest, then how many (the server checks). */
export default {
  name: 'TreesDialog',
  props: {
    mode: { type: String, required: true }, // 'assign' | 'release'
    company: { type: Object, required: true }, // { id, name }
  },
  data() {
    return { forests: [], loading: true, areaId: null, count: 1, busy: false, error: '' };
  },
  computed: {
    selected() { return this.forests.find((f) => f.areaId === this.areaId) || null; },
    /** Splits per tree, read from what the server sent. */
    perTree() { const f = this.forests[0]; return f && f.trees ? Math.round(f.splits / f.trees) : 0; },
    valid() {
      const n = Number(this.count);
      return !!this.selected && Number.isInteger(n) && n >= 1 && n <= this.selected.trees;
    },
  },
  async created() {
    try {
      const res = this.mode === 'assign' ? await Companies.assignableForests() : await Companies.releasableForests(this.company.id);
      this.forests = res.data || [];
    } catch (err) {
      this.error = apiError(err, 'Could not load the forests.');
    } finally {
      this.loading = false;
    }
  },
  methods: {
    fmt(v) { return new Intl.NumberFormat('en-GB').format(Number(v) || 0); },
    onHide(e) { if (this.busy) e.preventDefault(); },
    pick(id) { this.areaId = id; this.count = 1; this.error = ''; },
    async confirm() {
      const f = this.selected;
      if (!f || !this.valid || this.busy) return;
      this.busy = true;
      this.error = '';
      const n = Number(this.count);
      try {
        if (this.mode === 'assign') await Companies.assignTrees(this.company.id, f.areaId, n);
        else await Companies.releaseTrees(this.company.id, f.areaId, n);
        this.$emit('done', this.mode === 'assign'
          ? `${this.fmt(n)} tree${n === 1 ? '' : 's'} from ${f.forest} assigned to ${this.company.name}.`
          : `${this.fmt(n)} tree${n === 1 ? '' : 's'} from ${f.forest} released from ${this.company.name}.`);
      } catch (err) {
        this.error = apiError(err, 'Could not complete the operation.');
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import './dialogs.scss';
</style>
